import { chatSupabase } from '../lib/chatSupabase';

// ─── Auth ────────────────────────────────────────────────────────────────────

export const techSignIn = async (email, password) => {
  if (!chatSupabase) throw new Error('Chat Supabase not configured');
  const { data, error } = await chatSupabase?.auth?.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
};

export const techSignUp = async (email, password, metadata = {}) => {
  if (!chatSupabase) throw new Error('Chat Supabase not configured');
  const { data, error } = await chatSupabase?.auth?.signUp({
    email,
    password,
    options: { data: { ...metadata, role: 'technologist' } },
  });
  if (error) throw error;
  return data;
};

export const techSignOut = async () => {
  if (!chatSupabase) return;
  const { error } = await chatSupabase?.auth?.signOut();
  if (error) throw error;
};

export const getTechSession = async () => {
  if (!chatSupabase) return null;
  const { data } = await chatSupabase?.auth?.getSession();
  return data?.session ?? null;
};

export const onTechAuthChange = (callback) => {
  if (!chatSupabase) return { data: { subscription: { unsubscribe: () => {} } } };
  return chatSupabase?.auth?.onAuthStateChange(callback);
};

// ─── Conversations ────────────────────────────────────────────────────────────

export const getOrCreateConversation = async (doctorId, techId, doctorName, techName) => {
  if (!chatSupabase) throw new Error('Chat Supabase not configured');

  // Try to find existing conversation
  const { data: existing, error: fetchError } = await chatSupabase?.from('chat_conversations')?.select('*')?.or(`and(doctor_id.eq.${doctorId},tech_id.eq.${techId}),and(doctor_id.eq.${techId},tech_id.eq.${doctorId})`)?.single();

  if (existing) return existing;

  // Create new conversation
  const { data, error } = await chatSupabase?.from('chat_conversations')?.insert({
      doctor_id: doctorId,
      tech_id: techId,
      doctor_name: doctorName,
      tech_name: techName,
      created_at: new Date()?.toISOString(),
      updated_at: new Date()?.toISOString(),
    })?.select()?.single();

  if (error) throw error;
  return data;
};

export const getConversationsForUser = async (userId, role) => {
  if (!chatSupabase) throw new Error('Chat Supabase not configured');
  const column = role === 'doctor' ? 'doctor_id' : 'tech_id';
  const { data, error } = await chatSupabase?.from('chat_conversations')?.select('*')?.eq(column, userId)?.order('updated_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
};

// ─── Messages ─────────────────────────────────────────────────────────────────

export const getMessages = async (conversationId) => {
  if (!chatSupabase) throw new Error('Chat Supabase not configured');
  const { data, error } = await chatSupabase?.from('chat_messages')?.select('*')?.eq('conversation_id', conversationId)?.order('created_at', { ascending: true });
  if (error) throw error;
  return data ?? [];
};

export const sendMessage = async (conversationId, senderId, senderName, senderRole, content) => {
  if (!chatSupabase) throw new Error('Chat Supabase not configured');

  const { data, error } = await chatSupabase?.from('chat_messages')?.insert({
      conversation_id: conversationId,
      sender_id: senderId,
      sender_name: senderName,
      sender_role: senderRole,
      content,
      created_at: new Date()?.toISOString(),
    })?.select()?.single();

  if (error) throw error;

  // Update conversation updated_at
  await chatSupabase?.from('chat_conversations')?.update({ updated_at: new Date()?.toISOString(), last_message: content })?.eq('id', conversationId);

  return data;
};

export const subscribeToMessages = (conversationId, callback) => {
  if (!chatSupabase) return { unsubscribe: () => {} };
  const channel = chatSupabase?.channel(`messages:${conversationId}`)?.on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'chat_messages', filter: `conversation_id=eq.${conversationId}` },
      (payload) => callback(payload?.new)
    )?.subscribe();
  return channel;
};

export const unsubscribeFromMessages = (channel) => {
  if (!chatSupabase || !channel) return;
  chatSupabase?.removeChannel(channel);
};

// ─── Technologist Profiles ────────────────────────────────────────────────────

export const getTechProfile = async (userId) => {
  if (!chatSupabase) return null;
  const { data } = await chatSupabase?.from('tech_profiles')?.select('*')?.eq('id', userId)?.single();
  return data;
};

export const upsertTechProfile = async (userId, profile) => {
  if (!chatSupabase) throw new Error('Chat Supabase not configured');
  const { data, error } = await chatSupabase?.from('tech_profiles')?.upsert({ id: userId, ...profile, updated_at: new Date()?.toISOString() })?.select()?.single();
  if (error) throw error;
  return data;
};

export const getAllTechnologists = async () => {
  if (!chatSupabase) return [];
  const { data } = await chatSupabase?.from('tech_profiles')?.select('*')?.order('full_name', { ascending: true });
  return data ?? [];
};
